import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavbar from "examples/Navbars/TopNavbar";
import Footer from "examples/Footer";
import { useAuth } from "context/AuthContext";
import {
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import MDButton from "../MDButton";

interface OrgUser {
    member_id: string;
    email_address: string;
    name?: string;
    created_at: string;
    is_admin?: boolean;
    email_address_verified?: boolean;
    roles: { role_id: string }[];
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL


const OrganizationUsers: React.FC = () => {
    const { sessionToken } = useAuth();
    const [users, setUsers] = useState<OrgUser[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Invite modal state
    const [newUserEmail, setNewUserEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [inviteStatus, setInviteStatus] = useState<string | null>(null);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editUser, setEditUser] = useState<OrgUser | null>(null);
    const [editEmail, setEditEmail] = useState("");
    const [editName, setEditName] = useState("");
    const [editIsAdmin, setEditIsAdmin] = useState(false);

    // Function to fetch and update the users list
    const fetchUsers = async () => {
        if (!sessionToken) return;
        try {
            setIsLoading(true);
            const response = await fetch(
                `${API_BASE_URL}/account/users`,
                {
                    headers: {
                        Authorization: `Bearer ${sessionToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (!response.ok) throw new Error("Failed to fetch users");
            const data = await response.json();
            const formatted = data.members || [];
            setUsers(formatted);
            setError(null);
        } catch (err: any) {
            setError(err.message || "Unknown error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [sessionToken]);

    // Invite New User functionality
    const openInviteModal = () => {
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
        setNewUserEmail("");
        setFirstName("");
        setLastName("");
        setIsAdmin(false);
        setInviteStatus(null);
    };

    const handleSendInvite = async () => {
        if (!newUserEmail) {
            alert("Please enter an email address.");
            return;
        }
        setIsInviting(true);
        setInviteStatus(null);
        try {
            const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
            const response = await fetch(
                `${API_BASE_URL}/account/invite_user`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${sessionToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email_address: newUserEmail,
                        is_admin: isAdmin,
                        name: fullName,
                    }),
                }
            );
            const result = await response.json();
            if (response.ok) {
                setInviteStatus("Invitation sent successfully. Please check your email.");
                fetchUsers();
            } else {
                setInviteStatus(result.error || "Failed to send invitation.");
            }
        } catch (error) {
            setInviteStatus("Failed to send invitation.");
        } finally {
            setIsInviting(false);
        }
    };

    // Edit User functionality triggered by row click
    const handleRowClick = (row: any) => {
        const user = row.original;
        setEditUser(user);
        setEditEmail(user.email_address);
        setEditName(user.name || "");
        setEditIsAdmin(!!user.is_admin);
        setEditModalOpen(true);
    };

    const handleUpdateUser = async () => {
        if (!editUser) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/account/update_member`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${sessionToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        member_id: editUser.member_id,
                        email_address: editEmail,
                        name: editName,
                        is_admin: editIsAdmin,
                    }),
                }
            );
            const result = await response.json();
            if (response.ok) {
                alert("User updated successfully.");
                setEditModalOpen(false);
                fetchUsers();
            } else {
                alert(result.error || "Failed to update user.");
            }
        } catch (error) {
            alert("Failed to update user.");
        }
    };

    // Columns configuration (without the "Make Admin" and "Reset Password" columns)
    const columns = [
        { Header: "Email", accessor: "email_address" },
        { Header: "Name", accessor: "name" },
        { Header: "Admin", accessor: "is_admin" },
        { Header: "Email Verified", accessor: "email_verified" },
    ];

    // Map rows from API data
    const rows = users.map((user) => ({
        email_address: user.email_address,
        name: user.name || "—",
        is_admin: user.is_admin ? "Yes" : "No",
        email_verified: user.email_address_verified ? "Yes" : "No",
    }));

    return (
        <DashboardLayout>
            <TopNavbar />
            <MDBox mt={4} mb={4} mx={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                p={3}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <MDTypography variant="h5">Organization Users</MDTypography>
                                <MDButton variant="contained" color="primary" onClick={openInviteModal}>
                                    Invite New User
                                </MDButton>
                            </MDBox>
                            <MDBox px={3} pb={3}>
                                {isLoading ? (
                                    <MDTypography>Loading users...</MDTypography>
                                ) : error ? (
                                    <MDTypography color="error">Error: {error}</MDTypography>
                                ) : (
                                    <DataTable
                                        table={{ columns, rows }}
                                        isSorted={true}
                                        entriesPerPage={false}
                                        showTotalEntries={false}
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />

            {/* Invite New User Modal */}
            <Dialog open={inviteModalOpen} onClose={closeInviteModal}>
                <DialogTitle>Invite New User</DialogTitle>
                <DialogContent>
                    {inviteStatus ? (
                        <MDTypography>{inviteStatus}</MDTypography>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Email Address"
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                            <TextField
                                margin="dense"
                                label="First Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                            <TextField
                                margin="dense"
                                label="Last Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAdmin}
                                        onChange={(e) => setIsAdmin(e.target.checked)}
                                    />
                                }
                                label="Administrator"
                            />
                            <MDTypography variant="caption" display="block" gutterBottom>
                                Please enter the new user's email address and name. If you select "Administrator",
                                the user will have admin privileges.
                            </MDTypography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {inviteStatus ? (
                        <Button onClick={closeInviteModal}>Close</Button>
                    ) : (
                        <>
                            <Button onClick={closeInviteModal}>Cancel</Button>
                            <Button onClick={handleSendInvite} disabled={isInviting}>
                                Send Invitation
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Edit User Modal */}
            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editIsAdmin}
                                onChange={(e) => setEditIsAdmin(e.target.checked)}
                            />
                        }
                        label="Administrator"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateUser}>Save</Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default OrganizationUsers;
